import { useEffect, useLayoutEffect, useRef, useState } from "react";

export type RowMenuItem =
  | { icon: string; label: string; onClick: () => void; danger?: boolean }
  | "divider";

interface RowMenuProps {
  // the ⋮ button that was clicked; null closes the menu
  anchor: HTMLElement | null;
  title?: string;
  items: RowMenuItem[];
  onClose: () => void;
}

// Fixed-position action menu for table rows (never clipped by the table)
const RowMenu = ({ anchor, title, items, onClose }: RowMenuProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!anchor || !ref.current) return;
    const r = anchor.getBoundingClientRect();
    const mh = ref.current.offsetHeight;
    let top = r.bottom + 4;
    if (top + mh > window.innerHeight - 8) top = Math.max(8, r.top - mh - 4);
    setPos({ top, left: Math.max(8, r.right - 210) });
  }, [anchor, items]);

  useEffect(() => {
    if (!anchor) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current && !ref.current.contains(t) && !anchor.contains(t)) onClose();
    };
    // scrolling inside the (scrollable) menu itself must not close it
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [anchor, onClose]);

  if (!anchor) return null;
  return (
    <div className="cx-rmenu" ref={ref} style={{ top: pos.top, left: pos.left }}>
      {title && <div className="hd">{title}</div>}
      {items.map((it, i) =>
        it === "divider" ? <hr key={i} /> : (
          <button type="button" key={i} className={it.danger ? "dang" : ""} onClick={() => { onClose(); it.onClick(); }}>
            <i className={it.icon}></i>{it.label}
          </button>
        )
      )}
    </div>
  );
};

export default RowMenu;
